<!-- ****************************************************
*     		               NOTE                      	*
* At the moment this componente was implemented,    	*
* We used it as a Base for the following components:	*
* Amount, Generic, Password, Calendar                   *
* If you need to change deeply this component in   		*
* the future to work for a new component, STOP			*
*                                                       *
* DO NOT MAKE THIS FILE MORE COMPLEX THAN IT ALREADY IS *
***************************************************** -->
<template>
    <div
    ref="inputElementContainer"
    class="input-elements-container"
    :class="[{ 'error-state': params.errorState, 'warning-state': params.warningState }, inputStyle, getAppThemeMode]">
        <!-- *********************************************
        *                      Label                     *
        ********************************************** -->
        <label-generic
        v-if="params.labelText"
        :class="{
            'focused-label': inputFocused,
            'label-top': inputValue || (inputValue && inputValue ? inputValue.length : 0 !== 0),
        }"
        :labelHasMultipleLines="params.labelHasMultipleLines"
        :text="params.labelText"
        :mandatory="params.mandatory"
        :errorState="params.errorState"
        :tooltipInfo="params.tooltipInfo"
        :tooltipIcon="params.tooltipIcon"
        @tooltipLeftButtonClicked="emit('tooltipLeftButtonClicked')"
        @tooltipRightButtonClicked="emit('tooltipRightButtonClicked')"/>
        <!-- *********************************************
        *                      Input                     *
        ********************************************** -->
        <div
        class="input-content"
        :class="{
            'input-focused': inputFocused,
            'textarea': textarea,
            'with-left-icon': withLeftIcon,
            'with-right-button':  rightButtonIcon,
            'mobile': getMobileAppState
        }">
            <!-- Input slot -->
            <slot name="input"></slot>
            <!-- *********************************************
            *              Auxiliar Text Right               *
            ********************************************** -->
            <div v-if="params.auxiliarRightText" class='right-auxiliar-text'>{{params.auxiliarRightText}}</div>
        </div>
        <!-- *********************************************
        *               Right Icon Button                *
        ********************************************** -->
        <buttons-quaternary
        v-if="rightButtonIcon"
        class="right-icon-button"
        buttonSize="tiny"
		:buttonStyle="inputStyle === 'transparent' ? 'square-transparent': undefined"
        :icon="rightButtonIcon"
        :class="{'with-label': params.labelText}"
        @clicked="emit('rightButtonClicked')"/>
        <!-- *********************************************
        *                 Bottom elements                *
        ********************************************** -->
        <div
        v-if="params.bottomMessage || params.errorMessage || params.minValue || params.maxValue || params.showCounter"
        class="bottom-elements-container"
        :class="{'space-between': (params.bottomMessage || params.errorMessage || params.minValue || params.maxValue) && params.showCounter}">
            <div class="left-elements" :class="{'values-inline': params.bottomElementsInLine}">
                <!-- Simple Bottom Messages -->
                <div class='bottom-message' v-html="params.errorState ? params.errorMessage : params.bottomMessage"/>
                <!-- Min Value -->
                <div v-if="params.minValue && !params.errorMessage && !params.hasRangeSlider" class="bottom-message">
                    {{t('inputs.minLabel')}}   {{amountFormat(params.minValue, params.currency)}}{{params.bottomElementsInLine ? ';': ''}}
                </div>
                <!-- Max Value -->
                <div v-if="params.maxValue && !params.errorMessage && !params.hasRangeSlider" class="bottom-message">
                    {{t('inputs.maxLabel')}} {{amountFormat(params.maxValue, params.currency)}}
                </div>
            </div>
            <div v-if="params.showCounter" class="right-elements">
                <!-- Char Counter -->
                <div  v-if="params.showCounter" class='bottom-message counter'>{{ `${inputValue ? inputValue.length : 0} / ${props.params.maxLength}` }}</div>
            </div>
        </div>
    </div>
</template>

<script setup>
import LabelGeneric from '@/components/generic/labels/Generic'
import ButtonsQuaternary from '@/components/generic/buttons/Quaternary'
/* Vue */
import { ref, watch } from 'vue'
/* Assets */
import { checkIfElementIsVisible } from '@/assets/scripts/htmlManipulators'
import { amountFormat } from '@/assets/scripts/amounts'

/* Store */
import { storeToRefs } from 'pinia'
import { useProjectBaseStore } from '@/store/projectBase'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getMobileAppState, getKeyboardHeight, getAppThemeMode } = storeToRefs(useProjectBaseStore())

/* Emmitters */
const emit = defineEmits(['onFocus', 'onBlur', 'onEnter', 'input', 'clear', 'rightButtonClicked', 'tooltipLeftButtonClicked', 'tooltipRightButtonClicked'])

/** props received
@params {Object} with this params:
    - labelText {String}: label for textField
    - bottomMessage {String}: text to display in bottom elements
    - largeText { Boolean } makes input text larger
    - errorMessage {String}: text to display in bottom elements while in errorState
    - showCounter {Boolean}: property to display char counter
    - maxLength {Number}: maxlength for input
    - mandatory { Boolean } adds * to labelText
    - errorState {Boolean}: property to show if input has error or not
    - bottomElementsInLine { Boolean }
    - minValue {String, Number}
    - maxValue {String, Number}
    - tooltipInfo {Object} with:
        - title {String} - tooltip title
        - text {String} - tooltip text
        - description {String} - tooltip description
        - negative {Boolean} - prop to apply negative styles
        - size {String} can be [medium, small, tiny (default)]
        - leftButtonText {String} - text for left button, has default text if this is not passed
        - rightButtonText {String} - text for right button, has default text if this is not passed
        - applyPositionFixed {Boolean} - prop to force tooltipo to use fixed position
    - tooltipIcon {String} - icon name to trigger tooltip
@inputStyle {String} class to apply to input to apply different styles ['transparent']
@textarea { Boolean } To apply styles specific needed for textareas
@inputFocused { Boolean }
@withLeftIcon { Boolean } To fix the left padding
@rightButtonIcon { String } Icon to show right button
@inputValue {String, Number}: value to be displayed in input
*/
const props = defineProps({
    params: {
        type: Object,
        default: () => {
            return {}
        }
    },
	inputStyle: Boolean,
    textarea: Boolean,
    inputFocused: Boolean,
    withLeftIcon: Boolean,
    rightButtonIcon: String,
    inputValue: [String, Number]
})

const inputElementContainer = ref('')

watch(() => props.inputFocused, (newVal, oldVal) => {
    if (getMobileAppState.value && props.inputFocused && window.cordova) {
        checkIfElementIsVisible(inputElementContainer.value, getKeyboardHeight.value || 290)
    }
})
</script>

<style lang="scss" scoped>
.input-elements-container {
    width: 100%;

    display: flex;
    flex-direction: column;

    position: relative;

    :deep(.label-container) {
        position: relative;

        line-height: 16px;

        margin-bottom: 8px;

        transition: transform 0.5s;

        &.focused-label {
            color: var(--primary);
        }
    }

    .input-content {
        width: 100%;
        height: 40px;
        position: relative;

        display: flex;
        align-items: center;

        border: 1px solid var(--secondary-light-1);
        border-radius: 8px;
        padding: 0 16px;

        background-color: var(--secondary-light-4);

        :deep(input:not([type="range"])) {
            box-sizing: border-box;
            background-color:transparent;

            outline: none;
            border: none;
            resize: none;
            line-height: 40px;

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        :deep(input:not([type="range"])), :deep(textarea) {
            width: 100%;

            &::placeholder {
                opacity: 1;
                /* For firefox */
                @include H6-Regular;
                line-height: 40px;
                text-transform: none;
                color: var(--secondary);
            }

            &::-ms-input-placeholder {
                /* Internet Explorer 10-11 */
                @include H6-Regular;
                line-height: 40px;
                text-transform: none;
                color: var(--secondary);
            }

            &::-ms-input-placeholder {
                /* Microsoft Edge */
                @include H6-Regular;
                line-height: 40px;
                text-transform: none;
                color: var(--secondary);
            }

            &.apply-dark-background-variant {
                color: var(--white);
            }
        }

        &.textarea {
            height: auto;
            padding: 4px;

            :deep(textarea) {
                min-height: 62px;
                padding: 8px 12px;
                box-sizing: border-box;
                background-color:transparent;

                outline: none;
                border: none;
                resize: vertical;
                line-height: 18px;

                &::placeholder {
                    line-height: 18px;
                }

                &::-ms-input-placeholder {
                    line-height: 18px;
                }

                &::-ms-input-placeholder {
                    line-height: 18px;
                }
            }

            &.mobile {
                :deep(textarea) {
                    resize: none;
                }
            }
        }

        @media (hover: hover) and (pointer: fine) {
            &:hover {
                border-color: var(--secondary-dark-3);
            }
        }

        &:active {
            background-color: var(--secondary-light-4);
        }

        &.input-focused {
            border-color: var(--primary);
        }

        &.with-left-icon {
            padding-left: 44px;
        }

        &.with-right-button, &.right-auxiliar-text {
            padding-right: 56px;
        }

        .right-auxiliar-text {
            position: absolute;
            right: 16px;
            top: 50%;
            transform: translateY(-50%);

            @include H6-Regular;
            color: var(--secondary);
            text-transform: uppercase;
            pointer-events: none;
        }
    }

    .right-icon-button {
        top: 4px;
        right: 8px;

        position: absolute;

        &.with-label {
            top: 28px;
        }
    }

    .bottom-elements-container {
        min-height: 12px;
        width: 100%;
        padding-top: 8px;

        display: flex;

        &.space-between {
            justify-content: space-between;

            .right-elements {
                width: auto;
                flex-shrink: 0;
                margin-left: 8px;
            }
        }

        .left-elements, .right-elements {
            width: 100%;

            display: flex;
            flex-direction: column;

            &.values-inline {
                flex-direction: row;
                gap: 3px;
            }

            .bottom-message {
                @include H7-Regular;
                line-height: 16px;
                color: var(--secondary-dark-1);
                text-align: left;

                &.counter {
                    text-align: right;
                }
            }
        }
    }

	&.transparent {
		:deep(.label-container) {
			color: var(--secondary-light-4);

			&.focused-label {
				color: var(--secondary-light-4);
			}
		}

		.input-content {
			border-color: var(--secondary-light-1);
			background-color: transparent;

			:deep(input:not([type="range"])), :deep(textarea) {
				color: var(--secondary-light-4);

				&::placeholder {
					color: var(--secondary-light-1);
				}

				&::-ms-input-placeholder {
					/* Internet Explorer 10-11 */
					color: var(--secondary-light-1);
				}

				&::-ms-input-placeholder {
					/* Microsoft Edge */
					color: var(--secondary-light-1);
				}

				&.apply-dark-background-variant {
					color: var(--white);
				}
			}

			@media (hover: hover) and (pointer: fine) {
				&:hover {
					border-color: var(--secondary-light-1);
					background-color: rgba(var(--secondary-light-1-rgb), 0.24);
				}
			}

			&:active {
				background-color: rgba(var(--secondary-light-1-rgb), 0.32);
			}

			&.input-focused {
				border-color: var(--primary);
			}
		}

		&.dark {
			:deep(.label-container) {
				color: var(--secondary-dark-4);

				&.focused-label {
					color: var(--secondary-dark-4);
				}
			}

			.input-content {
				border-color: var(--secondary-dark-2);
				background-color: transparent;

				:deep(input:not([type="range"])), :deep(textarea) {
					color: var(--secondary-dark-4);

					&::placeholder {
						color: var(--secondary-dark-3);
					}

					&::-ms-input-placeholder {
						/* Internet Explorer 10-11 */
						color: var(--secondary-dark-3);
					}

					&::-ms-input-placeholder {
						/* Microsoft Edge */
						color: var(--secondary-dark-3);
					}
				}

				@media (hover: hover) and (pointer: fine) {
					&:hover {
						border-color: var(--secondary-dark-2);
						background-color: rgba(var(--secondary-dark-2-rgb), 0.24);
					}
				}

				&:active {
					background-color: rgba(var(--secondary-dark-2-rgb), 0.32);
				}

				&.input-focused {
					border-color: var(--primary);
				}
			}
		}
	}

    &.error-state {
        :deep(.label-container) {
            color: var(--negative);
        }

        .input-content {
            border-color: var(--negative);
        }

        .bottom-elements-container {
            .left-elements, .right-elements {
                .bottom-message {
                    color: var(--negative);
                }
            }
        }
    }

	&.warning-state {
        .input-content {
            border-color: var(--warning);
        }
		:deep(svg) {
			.change-color-fill {
				fill: var(--warning);
			}
		}

        .bottom-elements-container {
            .left-elements, .right-elements {
                .bottom-message {
                    color: var(--warning);
                }
            }
        }
    }
}
</style>
